/**
 * addToWaiting — pure helpers for the month-view waiting-list flip.
 *
 * Month view drives the wire `addToWaiting` flag off a single effective value
 * with a fixed precedence (see `computeEffectiveAddToWaiting`). These helpers
 * hold that logic outside the React component so it can be reasoned about and
 * tested in isolation.
 *
 * @module addToWaiting
 */

/**
 * Truthy across every wire shape the flag arrives in.
 *
 * The precheck response reaches month view via Symfony, which serializes
 * `addToWaiting` as a boolean `true`; legacy PHP shipped `1`. Accept both
 * plus the string `'1'`. Do NOT use `parseInt` — `parseInt(true, 10)` is
 * `NaN`, so a numeric mirror would never flip against the Symfony boolean.
 *
 * @param {*} v
 * @returns {boolean}
 */
export const isTruthy = v => v === true || v === 1 || v === '1';

/**
 * Seed the flip from an edited event's waiting-list status at open time.
 * `reserve` bool (list DTO) OR `additionalData.reserve === 1` (detail DTO,
 * WAITING_LIST enum; 2 = INSTRUCTOR is NOT reserve). Mirrors
 * `EventViewModal` reserve detection.
 *
 * @param {Object|null|undefined} event
 * @returns {boolean}
 */
export function deriveReserveSeed(event) {
	const ev = event || {};
	const ad = ev.additionalData || {};
	return isTruthy(ev.reserve) || Number(ad.reserve) === 1;
}

/**
 * Bidirectional precheck signal: `true` iff ANY currently-selected resource in
 * the precheck response carries a truthy `addToWaiting`; else `false`.
 * Bidirectional so the flag demotes when the slot frees up.
 *
 * Accepts the response `resources` as either an array (month-view Symfony
 * shape) or an id-keyed map (day-view shape).
 *
 * @param {Array<Object>|Object|null|undefined} resources
 * @param {Array<string|number>} selectedIds
 * @returns {boolean}
 */
export function scanResourcesAddToWaiting(resources, selectedIds) {
	const selected = new Set((selectedIds || []).map(String));
	if (selected.size === 0) return false;
	const list = Array.isArray(resources)
		? resources
		: resources && typeof resources === 'object'
			? Object.values(resources)
			: [];
	return list.some(item => {
		if (!item) return false;
		const id = item.resourceID ?? item.id ?? item.resourceId;
		if (id == null || !selected.has(String(id))) return false;
		return isTruthy(item.addToWaiting);
	});
}

/**
 * Single effective `addToWaiting` flag with precedence:
 *   forceAddToWaiting                                  (explicit create modal → always true)
 *   || (precheckSettled ? latestPrecheckSignal         (after first precheck → response wins)
 *                       : initialReserveSeed)           (before first precheck → seed from event)
 *
 * @param {Object} params
 * @param {boolean} params.forceAddToWaiting
 * @param {boolean} params.precheckSettled
 * @param {boolean} params.latestPrecheckSignal
 * @param {boolean} params.initialReserveSeed
 * @returns {boolean}
 */
export function computeEffectiveAddToWaiting({
	forceAddToWaiting,
	precheckSettled,
	latestPrecheckSignal,
	initialReserveSeed
}) {
	if (forceAddToWaiting) return true;
	return precheckSettled ? !!latestPrecheckSignal : !!initialReserveSeed;
}

/**
 * Submit-button label — legacy parity (`EventFormBody` original ternary).
 * Edit mode ALWAYS shows Save Event / Save Maintenance; the "Add to Waiting
 * List" label belongs only to the explicit create-into-waiting modal
 * (`forceAddToWaiting`). The reactive flip drives the wire payload, NOT the
 * label — an edited waiting-list event still reads "Save Event" and simply
 * saves onto the waiting list.
 *
 * @param {Object} params
 * @param {string} [params.submitLabel]  Explicit override — wins over all.
 * @param {boolean} params.forceAddToWaiting  Explicit create-into-waiting modal.
 * @param {boolean} params.isEditMode
 * @param {boolean} params.isMaintenance
 * @param {boolean} [params.willBeBackup]  Precheck says this add lands as a backup reservation.
 * @returns {string}
 */
export function resolveSubmitLabel({
	submitLabel,
	forceAddToWaiting,
	isEditMode,
	isMaintenance,
	willBeBackup
}) {
	if (submitLabel) return submitLabel;
	if (isEditMode) return isMaintenance ? 'Save Maintenance' : 'Save Event';
	if (forceAddToWaiting) return 'Add to Waiting List';
	if (isMaintenance) return 'Add Resource Maintenance';
	if (willBeBackup) return 'Add as Backup';
	return 'Add Event';
}
